import * as React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { type GpuStatsQuery } from "../api";
import { calculateQuotaUsagePercent, formatQuotaUsagePercent } from "../utils";

type Props = {
  gpu: GpuStatsQuery;
  hideUsageDetails?: boolean;
  showPercentage?: boolean;
};

export function TotalGpuHoursCard({
  gpu,
  hideUsageDetails = false,
  showPercentage = true,
}: Props) {
  const { t, i18n } = useTranslation();
  const quota = gpu.data?.usage?.quota;
  const used = quota?.used ?? 0;
  const total = quota?.total ?? 0;
  const isZeroUsage = used === 0;
  const usagePercent = calculateQuotaUsagePercent(used, total);
  const displayedPercent = formatQuotaUsagePercent(usagePercent);
  const periodEnd = quota?.period_end;
  const resetDate = React.useMemo(() => {
    if (!periodEnd) return "—";
    const date = new Date(periodEnd);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  }, [i18n.language, periodEnd]);

  if (!gpu.isPending && (gpu.isError || !gpu.data?.usage_available || !quota)) {
    return (
      <Card className="flex h-full flex-col gap-3 p-5">
        <span className="text-sm text-muted-foreground">
          {t("usageAndCost.stats.totalGpuHours")}
        </span>
        <p className="text-sm text-muted-foreground">
          {t("usageAndCost.gpuUnavailable")}
        </p>
        <button className="text-sm underline" onClick={() => gpu.refetch()}>
          {t("common.retry")}
        </button>
      </Card>
    );
  }

  return (
    <Card className="flex h-full flex-col gap-3 p-5">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {t("usageAndCost.stats.totalGpuHours")}
        </span>
        <Badge variant="default" className="capitalize">
          {t("usageAndCost.topDeployments.category")}
        </Badge>
      </div>

      {showPercentage && (gpu.isPending || (!isZeroUsage && !hideUsageDetails)) && (
        <div
          className="h-1 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={gpu.isPending ? undefined : usagePercent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-emerald-400"
            style={{ width: gpu.isPending ? "0%" : `${usagePercent}%` }}
          />
        </div>
      )}

      {gpu.isPending ? (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <Skeleton className="h-4 w-full" />
        </div>
      ) : (
        !isZeroUsage &&
        !hideUsageDetails && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            {showPercentage ? <span>{displayedPercent}</span> : <span />}
            <span>
              {t("usageAndCost.stats.resets")} {resetDate}
            </span>
          </div>
        )
      )}

      <div className="mt-auto flex items-baseline gap-2">
        {gpu.isPending ? (
          <Skeleton className="h-9 w-40" />
        ) : (
          <>
            <span className="text-3xl font-semibold tracking-tight text-foreground">
              {used.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
            <span className="text-sm text-muted-foreground">
              /{total.toLocaleString("en-US")} {t("usageAndCost.stats.hrs")}
            </span>
          </>
        )}
      </div>
    </Card>
  );
}
